import React, { useEffect, useState } from "react";
import { useBusinessContext } from "@/contexts/business/BusinessContext";

/**
 * Retail Ranker — First Analysis (the "wow moment").
 *
 * Structured around the Retail Ranker charter's reasoning engine:
 *   Customer Demand × Store Visibility × Business Context
 *     → Discoverability Intelligence → Prioritized Opportunities
 *
 * Five acts, staged one at a time:
 *   Act 1 — What customers are looking for  (demand signals)
 *   Act 2 — Where you stand on the four things that matter
 *           (Visibility · Trust · Relevance · Momentum)
 *   Act 3 — The gap (signature demand-vs-visibility reveal)
 *   Act 4 — Prioritized opportunities (with a "start here" pick)
 *   Act 5 — Handoff into the recurring home
 *
 * Copy is static placeholder — the shape mirrors the future
 * reasoning-engine output so the JSX won't have to change when it's wired.
 */

const serif = { fontFamily: '"Playfair Display", Georgia, serif' } as const;

type IndicatorKey = "visibility" | "trust" | "relevance" | "momentum";
type IndicatorScore = "weak" | "mixed" | "strong";
type Indicator = { score: IndicatorScore; verdict: string };

type DemandKind = "brand" | "product" | "service" | "event" | "seasonal";
type DemandSignal = { term: string; kind: DemandKind; note?: string };

type Opportunity = {
  title: string;
  why: string;
  effort: string;
  impact: string;
  confidence: string;
  lens: "Visibility" | "Trust" | "Relevance" | "Momentum";
};

type Analysis = {
  storeName: string;
  demandSignals: DemandSignal[];
  indicators: Record<IndicatorKey, Indicator>;
  gap: { headline: string; detail: string };
  opportunities: Opportunity[];
};

interface FirstAnalysisProps {
  onEnterHome?: () => void;
}

const FirstAnalysis: React.FC<FirstAnalysisProps> = ({ onEnterHome }) => {
  let currentBusinessName: string | undefined;
  try {
    const ctx = useBusinessContext() as any;
    currentBusinessName = ctx?.currentBusiness?.name;
  } catch {
    currentBusinessName = undefined;
  }

  const storeName = currentBusinessName || "The Salvaged Boutique";

  // Staged reveal — each act fades in after the previous.
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 1100, 2000, 2900, 3800, 4700].map((ms, i) =>
      window.setTimeout(() => setStage((s) => Math.max(s, i + 1)), ms)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  const analysis: Analysis = {
    storeName,
    demandSignals: [
      { term: "Nora Fleming", kind: "brand", note: "rising in your area" },
      { term: "Jellycat plush", kind: "brand" },
      { term: "MacKenzie-Childs", kind: "brand" },
      { term: "First communion gifts", kind: "seasonal", note: "peaks in 3 weeks" },
      { term: "Bridal shower gifts", kind: "event" },
      { term: "Personalized platters", kind: "product" },
      { term: "Gift wrapping", kind: "service" },
    ],
    indicators: {
      visibility: {
        score: "mixed",
        verdict:
          "You show up for your store name, but rarely for the things people search when they don't yet know you exist.",
      },
      trust: {
        score: "strong",
        verdict:
          "4.8 stars across 214 reviews, and faster response times than 92% of retailers in a 10-mile radius. This is a real asset.",
      },
      relevance: {
        score: "weak",
        verdict:
          "Local shoppers are searching for the brands and gift occasions you actually carry — but almost none of that language appears on your site.",
      },
      momentum: {
        score: "mixed",
        verdict:
          "Steady, but not accelerating. Nothing you've published in the last 90 days is picking up new visibility.",
      },
    },
    gap: {
      headline:
        "Your neighborhood is asking for exactly what you sell — and finding three other stores instead.",
      detail:
        "Searches for Nora Fleming, Jellycat and MacKenzie-Childs inside your trade area are up 34% year over year. Your store carries all three. None of those brand names appear anywhere on your site, your Google profile, or your recent posts. That is the gap Retail Ranker exists to close.",
    },
    opportunities: [
      {
        title: "Name the brands you carry on your homepage.",
        why: "The demand is already there. This is the smallest change with the largest signal — you become visible to shoppers already searching for these names.",
        effort: "~30 minutes",
        impact: "High impact",
        confidence: "High confidence",
        lens: "Relevance",
      },
      {
        title: "Publish a First Communion gift guide before April.",
        why: "Local searches for this occasion peak in three weeks. No competitor in your area is ranking for it yet.",
        effort: "~2 hours",
        impact: "High impact",
        confidence: "Medium confidence",
        lens: "Relevance",
      },
      {
        title: "Refresh your Google profile with three spring photos.",
        why: "Your profile still leads with winter product. Fresh imagery tends to lift map-pack impressions within a week.",
        effort: "~15 minutes",
        impact: "Medium impact",
        confidence: "High confidence",
        lens: "Visibility",
      },
      {
        title: "Reply to the last four unanswered reviews.",
        why: "Your trust score is already strong — protecting it is easier than rebuilding it. Response cadence is the piece slipping.",
        effort: "~10 minutes",
        impact: "Low impact",
        confidence: "High confidence",
        lens: "Trust",
      },
      {
        title: "Add a short post about your gift wrapping service.",
        why: "Nobody in your area is showing up for this, and it's a service you already offer for free. Pure upside.",
        effort: "~20 minutes",
        impact: "Medium impact",
        confidence: "Medium confidence",
        lens: "Momentum",
      },
    ],
  };

  const startHere = analysis.opportunities[0];
  const rest = analysis.opportunities.slice(1);

  return (
    <div
      className="min-h-screen w-full bg-background px-4 sm:px-8 py-14 lg:py-20"
      style={{ fontFamily: "Inter, system-ui, sans-serif" }}
    >
      <div className="mx-auto w-full max-w-4xl">
        {/* ── Reveal header ─────────────────────────────────────────── */}
        <Reveal show={stage >= 1}>
          <div className="flex items-center gap-3 mb-6">
            <PulseDot />
            <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--brass))]">
              Analysis complete
            </span>
            <span className="h-px w-10 bg-[hsl(var(--rule))]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--ink-faint))]">
              {analysis.storeName}
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-[64px] font-normal text-[hsl(var(--ink-deep))] leading-[1.02] tracking-tight"
            style={serif}
          >
            Here's what your neighborhood
            <br />
            is looking for.
          </h1>

          <p className="mt-6 text-[hsl(var(--ink-muted))] leading-relaxed text-[16px] max-w-[54ch]">
            Retail Ranker started with the question that matters most: what
            are shoppers in your area actually searching for right now? Then
            it compared that demand to how visible your store is for each of
            those things. Here's what we found.
          </p>
        </Reveal>

        {/* ── Act 1 — What customers are looking for ────────────────── */}
        <Reveal show={stage >= 2}>
          <section className="mt-20 pt-12 border-t border-[hsl(var(--rule))]">
            <ActLabel index="01" label="What customers are looking for" />
            <h2
              className="mt-6 text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] text-[hsl(var(--ink-deep))] tracking-tight max-w-[24ch]"
              style={serif}
            >
              The strongest signals in your trade area.
            </h2>
            <p className="mt-5 text-[hsl(var(--ink-muted))] leading-relaxed text-[15px] max-w-[58ch]">
              These are the brands, products, services and occasions your
              neighborhood searched for most in the last 90 days — not what
              you rank for, what they're already looking for.
            </p>
            <ul className="mt-10 divide-y divide-[hsl(var(--rule))] border-y border-[hsl(var(--rule))]">
              {analysis.demandSignals.map((s) => (
                <li key={s.term} className="flex items-baseline gap-6 py-4">
                  <span className="w-20 shrink-0 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-faint))] font-semibold">
                    {s.kind}
                  </span>
                  <span
                    className="flex-1 text-xl sm:text-2xl text-[hsl(var(--ink-deep))] leading-snug"
                    style={serif}
                  >
                    {s.term}
                  </span>
                  {s.note && (
                    <span className="hidden sm:inline text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--brass))]">
                      {s.note}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        {/* ── Act 2 — Where you stand ───────────────────────────────── */}
        <Reveal show={stage >= 3}>
          <section className="mt-20 pt-12 border-t border-[hsl(var(--rule))]">
            <ActLabel index="02" label="Where you stand" />
            <h2
              className="mt-6 text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] text-[hsl(var(--ink-deep))] tracking-tight max-w-[26ch]"
              style={serif}
            >
              Four things worth watching. Here's your read on each.
            </h2>

            <div className="mt-12 space-y-10">
              <IndicatorRow
                name="Visibility"
                score={analysis.indicators.visibility.score}
                verdict={analysis.indicators.visibility.verdict}
              />
              <IndicatorRow
                name="Trust"
                score={analysis.indicators.trust.score}
                verdict={analysis.indicators.trust.verdict}
              />
              <IndicatorRow
                name="Relevance"
                signature
                score={analysis.indicators.relevance.score}
                verdict={analysis.indicators.relevance.verdict}
              />
              <IndicatorRow
                name="Momentum"
                score={analysis.indicators.momentum.score}
                verdict={analysis.indicators.momentum.verdict}
              />
            </div>
          </section>
        </Reveal>

        {/* ── Act 3 — The gap ───────────────────────────────────────── */}
        <Reveal show={stage >= 4}>
          <section className="mt-20 pt-12 border-t border-[hsl(var(--rule))]">
            <ActLabel index="03" label="The gap" accent="brass" />
            <h2
              className="mt-6 text-3xl sm:text-4xl lg:text-[48px] leading-[1.08] text-[hsl(var(--ink-deep))] tracking-tight max-w-[22ch]"
              style={serif}
            >
              {analysis.gap.headline}
            </h2>
            <p className="mt-8 text-[17px] text-[hsl(var(--ink))] leading-[1.75] max-w-[62ch]">
              {analysis.gap.detail}
            </p>
          </section>
        </Reveal>

        {/* ── Act 4 — Prioritized opportunities ─────────────────────── */}
        <Reveal show={stage >= 5}>
          <section className="mt-20 pt-12 border-t border-[hsl(var(--rule))]">
            <ActLabel index="04" label="Prioritized opportunities" />
            <h2
              className="mt-6 text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] text-[hsl(var(--ink-deep))] tracking-tight max-w-[26ch]"
              style={serif}
            >
              Five moves, ranked. Start with the one at the top.
            </h2>

            {/* Start here — first opportunity elevated */}
            <div className="mt-10 bg-[hsl(var(--paper))] border border-[hsl(var(--rule))] shadow-[0_30px_80px_-40px_rgba(45,58,48,0.18)] p-8 lg:p-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-8 bg-[hsl(var(--brass))]" />
                <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--brass))]">
                  Start here
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-faint))]">
                  · {startHere.lens}
                </span>
              </div>
              <h3
                className="text-2xl sm:text-3xl lg:text-[34px] leading-[1.15] text-[hsl(var(--ink-deep))] tracking-tight"
                style={serif}
              >
                {startHere.title}
              </h3>
              <p className="mt-5 text-[hsl(var(--ink))] leading-[1.7] text-[16px] max-w-[58ch]">
                {startHere.why}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--ink-faint))] font-medium">
                <span>{startHere.effort}</span>
                <Dot />
                <span>{startHere.impact}</span>
                <Dot />
                <span>{startHere.confidence}</span>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <button className="px-6 py-3 bg-[hsl(var(--ink))] text-[hsl(var(--background))] text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[hsl(var(--ink-deep))] transition-colors">
                  Show me how
                </button>
                <button className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))] transition-colors border-b border-[hsl(var(--rule))] pb-1">
                  See the evidence
                </button>
              </div>
            </div>

            {/* Remaining opportunities */}
            <ol className="mt-12 space-y-8">
              {rest.map((op, i) => (
                <li key={op.title} className="grid grid-cols-12 gap-6">
                  <div className="col-span-12 sm:col-span-2">
                    <div
                      className="text-[hsl(var(--ink-faint))] text-[11px] tracking-[0.28em] font-semibold"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {String(i + 2).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-10">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-faint))]">
                        {op.lens}
                      </span>
                    </div>
                    <h3
                      className="text-xl sm:text-2xl lg:text-[26px] leading-[1.25] text-[hsl(var(--ink-deep))] tracking-tight"
                      style={serif}
                    >
                      {op.title}
                    </h3>
                    <p className="mt-3 text-[15px] text-[hsl(var(--ink-muted))] leading-relaxed max-w-[58ch]">
                      {op.why}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--ink-faint))] font-medium">
                      <span>{op.effort}</span>
                      <Dot />
                      <span>{op.impact}</span>
                      <Dot />
                      <span>{op.confidence}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </Reveal>

        {/* ── Act 5 — Handoff ───────────────────────────────────────── */}
        <Reveal show={stage >= 6}>
          <section className="mt-24 pt-14 border-t border-[hsl(var(--rule))]">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-8 bg-[hsl(var(--brass))]" />
              <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--brass))]">
                From here on
              </span>
            </div>
            <p
              className="text-2xl sm:text-3xl lg:text-[36px] leading-[1.25] text-[hsl(var(--ink-deep))] tracking-tight max-w-[36ch]"
              style={serif}
            >
              Retail Ranker keeps watching this quietly for you.
            </p>
            <p
              className="mt-4 text-xl sm:text-2xl leading-[1.4] text-[hsl(var(--ink-muted))] max-w-[42ch]"
              style={serif}
            >
              You'll come back to a short weekly read — new demand, new
              gaps, and what to do about them. No dashboards. No noise.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
              <span className="text-[11px] uppercase tracking-[0.25em] text-[hsl(var(--ink-faint))]">
                Yours to keep, print, or share.
              </span>
              <button
                onClick={onEnterHome}
                className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[hsl(var(--ink))] hover:text-[hsl(var(--brass))] transition-colors"
              >
                Continue to home →
              </button>
            </div>
          </section>
        </Reveal>
      </div>
    </div>
  );
};

/* ── Building blocks ───────────────────────────────────────────── */

const Reveal: React.FC<{ show: boolean; children: React.ReactNode }> = ({
  show,
  children,
}) => (
  <div
    style={{
      opacity: show ? 1 : 0,
      transform: show ? "translateY(0)" : "translateY(12px)",
      transition: "opacity 700ms ease-out, transform 700ms ease-out",
    }}
  >
    {children}
  </div>
);

const PulseDot: React.FC = () => (
  <span className="relative inline-flex h-2 w-2">
    <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--brass))] opacity-60 animate-ping" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--brass))]" />
  </span>
);

const Dot: React.FC = () => (
  <span className="h-1 w-1 rounded-full bg-[hsl(var(--brass))]" />
);

const ActLabel: React.FC<{
  index: string;
  label: string;
  accent?: "brass" | "ink";
}> = ({ index, label, accent = "ink" }) => {
  const rule =
    accent === "brass" ? "bg-[hsl(var(--brass))]" : "bg-[hsl(var(--ink))]";
  const text =
    accent === "brass" ? "text-[hsl(var(--brass))]" : "text-[hsl(var(--ink))]";
  return (
    <div className="flex items-center gap-3">
      <span
        className="text-[hsl(var(--ink-faint))] text-[11px] tracking-[0.28em] font-semibold"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {index}
      </span>
      <span className={`h-px w-8 ${rule}`} />
      <span
        className={`text-[10px] uppercase tracking-[0.28em] font-semibold ${text}`}
      >
        {label}
      </span>
    </div>
  );
};

const IndicatorRow: React.FC<{
  name: string;
  score: IndicatorScore;
  verdict: string;
  signature?: boolean;
}> = ({ name, score, verdict, signature }) => {
  return (
    <article
      className={`grid grid-cols-12 gap-6 ${
        signature ? "border-l-2 border-[hsl(var(--brass))] pl-6 -ml-6" : ""
      }`}
    >
      <div className="col-span-12 sm:col-span-4">
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--ink))]">
            {name}
          </span>
          {signature && (
            <span className="text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--brass))]">
              · signature
            </span>
          )}
        </div>
        <ScoreMeter score={score} />
      </div>
      <div className="col-span-12 sm:col-span-8">
        <p
          className="text-xl sm:text-2xl leading-[1.35] text-[hsl(var(--ink-deep))] tracking-tight"
          style={serif}
        >
          {verdict}
        </p>
      </div>
    </article>
  );
};

const ScoreMeter: React.FC<{ score: IndicatorScore }> = ({ score }) => {
  const filled = score === "strong" ? 3 : score === "mixed" ? 2 : 1;
  const label =
    score === "strong" ? "Strong" : score === "mixed" ? "Mixed" : "Needs work";
  return (
    <div className="mt-4">
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`h-1.5 w-8 ${
              i < filled ? "bg-[hsl(var(--ink))]" : "bg-[hsl(var(--rule))]"
            }`}
          />
        ))}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-faint))] font-semibold">
        {label}
      </div>
    </div>
  );
};

export default FirstAnalysis;